import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import { useProducts } from '../../context/ProductsContext';
import {
  DAYS_OF_WEEK,
  DELIVERY_FEE_MODES,
  PACK_HAS_SUBSCRIBERS_MSG,
  PACK_KINDS,
  packHasActiveSubscriptions,
} from '../../api/subscriptions';
import form from '../../styles/form.module.css';
import styles from './Subscriptions.module.css';

const empty = {
  name: '',
  kind: '',
  itemId: '',
  qtyPerDrop: 1,
  price: '',
  discountPercent: 0,
  deliveryFee: '',
  deliveryFeeMode: 'PER_DAY',
  daysOfWeekDefault: DAYS_OF_WEEK.map((d) => d.value),
  description: '',
  status: 'ACTIVE',
};

export default function PackFormDrawer({ open, onClose, onSave, pack, saving, error }) {
  const { products: catalog } = useProducts();
  const [data, setData] = useState(empty);
  const [query, setQuery] = useState('');
  const isEdit = !!pack;
  const hasSubscribers = isEdit && packHasActiveSubscriptions(pack);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    if (pack) {
      setData({
        ...empty,
        ...pack,
        price: pack.price ?? '',
        deliveryFee: pack.deliveryFee ?? '',
        discountPercent: pack.discountPercent ?? 0,
        daysOfWeekDefault: pack.daysOfWeekDefault?.length
          ? pack.daysOfWeekDefault
          : empty.daysOfWeekDefault,
      });
    } else {
      setData(empty);
    }
  }, [pack, open]);

  const set = (key) => (e) => setData((d) => ({ ...d, [key]: e.target.value }));

  const selectedProduct = useMemo(
    () => catalog.find((p) => p.id === data.itemId) || null,
    [catalog, data.itemId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = catalog.filter((p) => p.status === 'ACTIVE');
    if (!q) return list.slice(0, 40);
    return list
      .filter((p) => `${p.name} ${p.sku || ''}`.toLowerCase().includes(q))
      .slice(0, 40);
  }, [catalog, query]);

  // const toggleDay = (day) => {
  //   setData((d) => {
  //     const has = d.daysOfWeekDefault.includes(day);
  //     return {
  //       ...d,
  //       daysOfWeekDefault: has
  //         ? d.daysOfWeekDefault.filter((x) => x !== day)
  //         : [...d.daysOfWeekDefault, day],
  //     };
  //   });
  // };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!data.itemId || !data.kind || !data.daysOfWeekDefault.length) return;
    const payload = {
      ...data,
      name: data.name.trim() || selectedProduct?.name || '',
      price: Number(data.price) || 0,
      discountPercent: Number(data.discountPercent) || 0,
      deliveryFee: Number(data.deliveryFee) || 0,
    };
    // Qty per drop is fixed at create — don't send it on edit.
    if (isEdit) {
      delete payload.qtyPerDrop;
    } else {
      payload.qtyPerDrop = Number(data.qtyPerDrop) || 1;
    }
    onSave(payload);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit pack' : 'Create pack'}
      subtitle="Product offer customers subscribe to — price, fee mode, and default schedule"
      width={460}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={saving || !data.itemId || !data.kind || !data.daysOfWeekDefault.length}
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create pack'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <p className={form.hint} style={{ color: 'var(--color-error)', marginBottom: 14 }}>
            {error}
          </p>
        )}

        <label className={form.field}>
          <span className={form.label}>Pack name</span>
          <input
            className={form.input}
            required
            value={data.name}
            onChange={set('name')}
            placeholder="Cow milk 500ml"
          />
        </label>

        <label className={form.field}>
          <span className={form.label}>Kind</span>
          <select
            className={form.select}
            required={!isEdit}
            value={data.kind}
            onChange={set('kind')}
          >
            {!isEdit && <option value="" disabled>Select kind</option>}
            {PACK_KINDS.map((k) => (
              <option key={k.value} value={k.value}>{k.label}</option>
            ))}
          </select>
        </label>

        <div className={form.field}>
          <span className={form.label}>Catalog item</span>
          {isEdit ? (
            <>
              <div className={`${styles.selectedItem} ${styles.selectedItemLocked}`}>
                <span>{selectedProduct?.name || pack?.itemId || '—'}</span>
              </div>
              <span className={form.hint}>Catalog item can’t be changed after the pack is created.</span>
            </>
          ) : (
            <>
              {selectedProduct && (
                <div className={styles.selectedItem}>
                  <span>{selectedProduct.name}</span>
                  <button
                    type="button"
                    className={styles.clearItem}
                    onClick={() => setData((d) => ({ ...d, itemId: '' }))}
                  >
                    Change
                  </button>
                </div>
              )}
              {!selectedProduct && (
                <>
                  <div className={styles.searchWrap}>
                    <Search size={14} className={styles.searchIcon} />
                    <input
                      className={form.input}
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search products…"
                      style={{ paddingLeft: 32 }}
                    />
                  </div>
                  <div className={styles.itemList}>
                    {filtered.length === 0 && (
                      <p className={form.hint} style={{ margin: '8px 0' }}>No matching products.</p>
                    )}
                    {filtered.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className={styles.itemRow}
                        onClick={() => setData((d) => ({
                          ...d,
                          itemId: p.id,
                          name: d.name || p.name,
                          price: d.price === '' ? (p.price ?? p.sellingPrice ?? '') : d.price,
                        }))}
                      >
                        <span className={styles.itemName}>{p.name}</span>
                        {p.sku && <span className={styles.itemSku}>{p.sku}</span>}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Unit price</span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input
                className={form.input}
                type="number"
                required
                min="0"
                step="0.01"
                value={data.price}
                onChange={set('price')}
              />
            </div>
          </label>
          <label className={form.field}>
            <span className={form.label}>Discount %</span>
            <input
              className={form.input}
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={data.discountPercent}
              onChange={set('discountPercent')}
            />
          </label>
        </div>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Delivery fee</span>
            <div className={form.prefixInput}>
              <span>₹</span>
              <input
                className={form.input}
                type="number"
                min="0"
                step="0.01"
                value={data.deliveryFee}
                onChange={set('deliveryFee')}
              />
            </div>
          </label>
          <label className={form.field}>
            <span className={form.label}>Fee mode</span>
            <select
              className={form.select}
              value={data.deliveryFeeMode}
              onChange={set('deliveryFeeMode')}
            >
              {DELIVERY_FEE_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Default days of week — hidden for now; create still sends all weekdays as default.
        <div className={form.field}>
          <span className={form.label}>Default days of week</span>
          <div className={styles.dayRow}>
            {DAYS_OF_WEEK.map((d) => {
              const on = data.daysOfWeekDefault.includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  className={`${styles.dayChip} ${on ? styles.dayChipOn : ''}`}
                  onClick={() => toggleDay(d.value)}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
          <span className={form.hint}>Used when customers pick a month schedule.</span>
        </div>
        */}

        <label className={form.field}>
          <span className={form.label}>Description (optional)</span>
          <textarea
            className={form.textarea}
            rows={2}
            value={data.description}
            onChange={set('description')}
            placeholder="Short pack copy for the customer app"
          />
        </label>

        {isEdit && (
          <label className={form.field}>
            <span className={form.label}>Status</span>
            <select
              className={form.select}
              value={data.status}
              onChange={set('status')}
              disabled={hasSubscribers && data.status === 'ACTIVE'}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE" disabled={hasSubscribers}>Inactive</option>
            </select>
            {hasSubscribers && data.status === 'ACTIVE' && (
              <span className={form.hint}>{PACK_HAS_SUBSCRIBERS_MSG}</span>
            )}
          </label>
        )}
      </form>
    </Drawer>
  );
}
